/**
 * AdminPricingPlans — super-admin CRUD for subscription plans, grouped by scope.
 *
 * Writes via /api/superadmin/plans (create/update/delete/toggle-active).
 * Groups visually by scope: school / event / church / ministry / standalone.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, Plus, Edit, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const SCOPES = ['school', 'event', 'church', 'ministry', 'standalone'];
const TIERS = ['trial', 'basic', 'standard', 'premium', 'enterprise', 'custom'];
const SCOPE_LABEL = { school: 'Schools', event: 'Events', church: 'Churches', ministry: 'Ministries', standalone: 'Standalone' };

const emptyForm = {
  code: '', name: '', tier: 'basic', scope: 'school',
  price_monthly: 0, price_yearly: 0, currency: 'USD',
  description: '', is_active: true, branding_allowed: false, api_access: false,
  max_students: null, max_staff: null, max_admins: null, max_storage_mb: null,
};

const AdminPricingPlans = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // 'new' | plan object
  const [form, setForm] = useState(emptyForm);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['plans-all'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/superadmin/plans?include_inactive=true`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed to load plans');
      return r.json();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const isNew = dialog === 'new';
      const url = isNew ? `${API_URL}/api/superadmin/plans` : `${API_URL}/api/superadmin/plans/${dialog.id || dialog._id || dialog.code}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(form) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Plan saved');
      qc.invalidateQueries({ queryKey: ['plans-all'] });
      setDialog(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (plan) => {
      const id = plan.id || plan._id || plan.code;
      const r = await fetch(`${API_URL}/api/superadmin/plans/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) throw new Error('Delete failed');
      return r.json();
    },
    onSuccess: () => {
      toast.success('Plan deleted');
      qc.invalidateQueries({ queryKey: ['plans-all'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const openNew = () => { setForm(emptyForm); setDialog('new'); };
  const openEdit = (p) => {
    setForm({
      ...emptyForm, ...p,
      max_students: p.max_students ?? null,
      max_staff: p.max_staff ?? null,
      max_admins: p.max_admins ?? null,
      max_storage_mb: p.max_storage_mb ?? null,
    });
    setDialog(p);
  };

  const grouped = SCOPES.reduce((acc, s) => {
    acc[s] = plans.filter(p => (p.scope || 'school') === s);
    return acc;
  }, {});

  return (
    <DashboardLayout title="Pricing Plans">
      <div className="space-y-5" data-testid="admin-pricing-plans-page">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" /> Pricing Plans
              </h1>
              <p className="text-xs text-slate-500">Grouped by scope — public /pricing page reads these</p>
            </div>
          </div>
          <Button onClick={openNew} data-testid="pricing-new-plan-btn">
            <Plus className="h-4 w-4 mr-1" /> New plan
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : SCOPES.map(scope => (
          <Card key={scope} data-testid={`pricing-scope-section-${scope}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <span>{SCOPE_LABEL[scope]}</span>
                <Badge variant="outline" className="text-[10px]">{grouped[scope].length} plan{grouped[scope].length === 1 ? '' : 's'}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {grouped[scope].length === 0 ? (
                <p className="text-xs text-slate-400 py-2">No plans yet for {SCOPE_LABEL[scope]}.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grouped[scope].map(p => (
                    <div key={p.code} className="border rounded-lg p-3 bg-white relative" data-testid={`pricing-admin-card-${p.code}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm truncate">{p.name}</span>
                            {!p.is_active && <Badge variant="outline" className="text-[10px] bg-slate-50">inactive</Badge>}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono">{p.code} · {p.tier}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)} data-testid={`pricing-edit-${p.code}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => { if (window.confirm(`Delete plan "${p.name}"?`)) del.mutate(p); }} data-testid={`pricing-del-${p.code}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 flex items-baseline gap-1">
                        <span className="text-lg font-bold">${p.price_monthly}</span>
                        <span className="text-xs text-slate-500">/mo</span>
                        {p.price_yearly > 0 && <span className="text-[10px] text-slate-400 ml-2">·  ${p.price_yearly}/yr</span>}
                      </div>
                      {p.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Plan dialog */}
        <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pricing-plan-dialog">
            <DialogHeader>
              <DialogTitle>{dialog === 'new' ? 'New plan' : `Edit ${form.name}`}</DialogTitle>
              <DialogDescription>Plans show on /pricing once scope is set and is_active is true.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code (unique)</Label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} disabled={dialog !== 'new'} data-testid="pricing-form-code" />
              </div>
              <div><Label>Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="pricing-form-name" />
              </div>
              <div><Label>Scope</Label>
                <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="pricing-form-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCOPES.map(s => <SelectItem key={s} value={s}>{SCOPE_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tier</Label>
                <Select value={form.tier} onValueChange={v => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="pricing-form-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Price / month</Label>
                <Input type="number" value={form.price_monthly} onChange={e => setForm({ ...form, price_monthly: Number(e.target.value) })} data-testid="pricing-form-month" />
              </div>
              <div><Label>Price / year</Label>
                <Input type="number" value={form.price_yearly} onChange={e => setForm({ ...form, price_yearly: Number(e.target.value) })} data-testid="pricing-form-year" />
              </div>
              <div className="col-span-2"><Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <div className="col-span-2 flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="pricing-form-active" />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.branding_allowed} onCheckedChange={v => setForm({ ...form, branding_allowed: v })} />
                  Custom branding
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.api_access} onCheckedChange={v => setForm({ ...form, api_access: v })} />
                  API access
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="pricing-form-save">
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminPricingPlans;
